import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Toolbar,
  Switch,
  FormControlLabel,
  Tooltip,
  FormHelperText,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import aiConfigurationService, { 
  AIConfiguration, 
  CreateAIConfigurationData, 
  UpdateAIConfigurationData,
  PlatformInfo
} from '../../services/aiConfigurationService';

const AIConfigurationPage: React.FC = () => {
  const { canManageUsers } = useAuth();
  const [configurations, setConfigurations] = useState<AIConfiguration[]>([]);
  const [platforms, setPlatforms] = useState<Record<string, PlatformInfo>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedConfiguration, setSelectedConfiguration] = useState<AIConfiguration | null>(null);
  
  // Form data
  const [createFormData, setCreateFormData] = useState<CreateAIConfigurationData>({
    platform: '',
    model: '',
    api_key: '',
    is_active: false
  });
  
  const [editFormData, setEditFormData] = useState<UpdateAIConfigurationData>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Load configurations
  const loadConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await aiConfigurationService.getConfigurations();
      setConfigurations(response.data.configurations);
      setPlatforms(response.data.platforms);
    } catch (err: any) {
      setError(err.message || 'Có lỗi xảy ra khi tải cấu hình AI');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigurations();
  }, [loadConfigurations]);

  // Check permissions
  if (!canManageUsers()) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Bạn không có quyền truy cập trang này.
        </Alert>
      </Container>
    );
  }

  // Handle create configuration
  const handleCreateConfiguration = async () => {
    try {
      setFormErrors({});
      const response = await aiConfigurationService.createConfiguration(createFormData);
      setSuccess(response.message);
      setCreateDialogOpen(false);
      setCreateFormData({ platform: '', model: '', api_key: '', is_active: false });
      loadConfigurations();
    } catch (err: any) {
      if (err.errors) {
        setFormErrors(err.errors);
      } else {
        setError(err.message || 'Có lỗi xảy ra khi tạo cấu hình AI');
      }
    }
  };

  // Handle edit configuration
  const handleEditConfiguration = (configuration: AIConfiguration) => {
    setSelectedConfiguration(configuration);
    setEditFormData({
      platform: configuration.platform,
      model: configuration.model,
      is_active: configuration.is_active,
      config: configuration.config
    });
    setEditDialogOpen(true);
  };

  const handleUpdateConfiguration = async () => {
    if (!selectedConfiguration) return;
    
    try {
      setFormErrors({});
      const response = await aiConfigurationService.updateConfiguration(selectedConfiguration.id, editFormData);
      setSuccess(response.message);
      setEditDialogOpen(false);
      setSelectedConfiguration(null);
      setEditFormData({});
      loadConfigurations();
    } catch (err: any) {
      if (err.errors) {
        setFormErrors(err.errors);
      } else {
        setError(err.message || 'Có lỗi xảy ra khi cập nhật cấu hình AI');
      }
    }
  };

  // Handle delete configuration
  const handleDeleteConfiguration = (configuration: AIConfiguration) => {
    setSelectedConfiguration(configuration);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedConfiguration) return;
    
    try {
      const response = await aiConfigurationService.deleteConfiguration(selectedConfiguration.id);
      setSuccess(response.message);
      setDeleteDialogOpen(false);
      setSelectedConfiguration(null);
      loadConfigurations();
    } catch (err: any) {
      setError(err.message || 'Có lỗi xảy ra khi xóa cấu hình AI');
    }
  };

  // Handle activate configuration
  const handleActivateConfiguration = async (configuration: AIConfiguration) => {
    try {
      const response = await aiConfigurationService.activateConfiguration(configuration.id);
      setSuccess(response.message);
      loadConfigurations();
    } catch (err: any) {
      setError(err.message || 'Có lỗi xảy ra khi kích hoạt cấu hình AI');
    }
  };

  // Handle platform change
  const handlePlatformChange = (platform: string) => {
    setCreateFormData(prev => ({
      ...prev,
      platform,
      model: '' // Reset model when platform changes
    }));
  };

  // Handle model change
  const handleModelChange = (model: string) => {
    setCreateFormData(prev => ({
      ...prev,
      model
    }));
  };

  // Clear messages
  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // Get platform display name
  const getPlatformDisplayName = (platform: string) => {
    return platforms[platform]?.name || platform;
  };

  // Get model display name
  const getModelDisplayName = (model: string) => {
    return model;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Quản lý Cấu hình AI
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Thêm Cấu hình
        </Button>
      </Box>

      {/* Messages */}
      {error && (
        <Alert severity="error" onClose={clearMessages} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={clearMessages} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Configurations Grid */}
      <Grid container spacing={3}>
        {configurations.map((config) => (
          <Grid key={config.id} sx={{ width: { xs: '100%', md: '50%', lg: '33.33%' } }}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" component="h2">
                    {getPlatformDisplayName(config.platform)}
                  </Typography>
                  {config.is_active ? (
                    <Chip 
                      icon={<CheckCircleIcon />} 
                      label="Đang hoạt động" 
                      color="success" 
                      size="small" 
                    />
                  ) : (
                    <Chip 
                      icon={<CancelIcon />} 
                      label="Không hoạt động" 
                      color="default" 
                      size="small" 
                    />
                  )}
                </Box>
                
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Model: {getModelDisplayName(config.model)}
                </Typography>
                
                <Typography variant="body2" color="text.secondary">
                  Tạo lúc: {new Date(config.created_at).toLocaleDateString('vi-VN')}
                </Typography>
              </CardContent>
              
              <CardActions>
                {!config.is_active && (
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => handleActivateConfiguration(config)}
                  >
                    Kích hoạt
                  </Button>
                )}
                <Button
                  size="small"
                  onClick={() => handleEditConfiguration(config)}
                >
                  Chỉnh sửa
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleDeleteConfiguration(config)}
                >
                  Xóa
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Empty state */}
      {configurations.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" mb={2}>
            Chưa có cấu hình AI nào
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hãy thêm cấu hình AI đầu tiên để bắt đầu sử dụng
          </Typography>
        </Paper>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Thêm Cấu hình AI</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="platform-label">Platform</InputLabel>
              <Select
                labelId="platform-label"
                label="Platform"
                value={createFormData.platform}
                onChange={(e) => handlePlatformChange(e.target.value)}
                error={!!formErrors.platform}
              >
                {Object.entries(platforms).map(([key, platform]) => (
                  <MenuItem key={key} value={key}>
                    {platform.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.platform && (
                <FormHelperText error>{formErrors.platform}</FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="model-label">Model</InputLabel>
              <Select
                labelId="model-label"
                label="Model"
                value={createFormData.model}
                onChange={(e) => handleModelChange(e.target.value)}
                error={!!formErrors.model}
                disabled={!createFormData.platform}
              >
                {createFormData.platform && platforms[createFormData.platform]?.models.map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.model && (
                <FormHelperText error>{formErrors.model}</FormHelperText>
              )}
            </FormControl>

            <TextField
              fullWidth
              label="API Key"
              type="password"
              value={createFormData.api_key}
              onChange={(e) => setCreateFormData(prev => ({ ...prev, api_key: e.target.value }))}
              error={!!formErrors.api_key}
              helperText={formErrors.api_key}
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={createFormData.is_active}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                />
              }
              label="Kích hoạt ngay"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleCreateConfiguration} variant="contained">
            Tạo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Chỉnh sửa Cấu hình AI</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="edit-platform-label">Platform</InputLabel>
              <Select
                labelId="edit-platform-label"
                label="Platform"
                value={editFormData.platform || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, platform: e.target.value }))}
                error={!!formErrors.platform}
              >
                {Object.entries(platforms).map(([key, platform]) => (
                  <MenuItem key={key} value={key}>
                    {platform.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.platform && (
                <FormHelperText error>{formErrors.platform}</FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="edit-model-label">Model</InputLabel>
              <Select
                labelId="edit-model-label"
                label="Model"
                value={editFormData.model || ''}
                onChange={(e) => setEditFormData(prev => ({ ...prev, model: e.target.value }))}
                error={!!formErrors.model}
              >
                {editFormData.platform && platforms[editFormData.platform]?.models.map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.model && (
                <FormHelperText error>{formErrors.model}</FormHelperText>
              )}
            </FormControl>

            <TextField
              fullWidth
              label="API Key (để trống nếu không thay đổi)"
              type="password"
              value={editFormData.api_key || ''}
              onChange={(e) => setEditFormData(prev => ({ ...prev, api_key: e.target.value }))}
              error={!!formErrors.api_key}
              helperText={formErrors.api_key}
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.is_active || false}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                />
              }
              label="Kích hoạt"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleUpdateConfiguration} variant="contained">
            Cập nhật
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa cấu hình AI này không? Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AIConfigurationPage; 